"use client";

import { useCallback } from "react";

import { useChatJumpStore } from "@/modules/chat/stores/chat-jump-store";
import { useChatSheetStore } from "@/modules/chat/stores/chat-sheet-store";
import { usePayBreakdownStore } from "@/modules/salaries/stores/pay-breakdown-store";
import { logger } from "@/lib/logger";

import type { WidgetAction } from "@/types";

/**
 * Runs a widget's non-navigational action.
 *
 * The dashboard's counterpart to useOpenNotification, and deliberately the same
 * mechanism: open the chat sheet, then name the room. Chat has no route, so there
 * is no href either flow could have used — the notification bell has always done
 * it this way, and the recent-messages widget was the odd one out for inventing a
 * `/chat` path that nothing serves.
 *
 * `target` is the row's id, which for a chat row is the Rocket room id. Omitted
 * for a card-level action, which just opens the panel and lets the user choose.
 */
export function useWidgetAction() {
  const openSheet = useChatSheetStore((s) => s.setOpen);
  const openRoom = useChatJumpStore((s) => s.openRoom);
  const openPayBreakdown = usePayBreakdownStore((s) => s.openPreview);
  const openCheckBreakdown = usePayBreakdownStore((s) => s.openCheck);

  return useCallback(
    (action: WidgetAction, target?: string) => {
      switch (action) {
        case "open_chat":
          // Order matters only in that both happen before ChatShell mounts:
          // it reads the pending room in its initializer, so the room has to be
          // set by the time the sheet renders.
          if (target) openRoom(target);
          openSheet(true);
          return;

        case "open_pay_breakdown":
          // No target: the endpoint behind the panel is scoped to the caller, so
          // there is no id to pass and nothing for one to widen.
          openPayBreakdown();
          return;

        case "open_check_breakdown":
          // The target is the row's id, which for a My-checks row is the salary
          // uuid. Without one there is nothing to show, so don't open an empty
          // panel — a bundle predating the backend's payload would do exactly
          // that.
          if (target) openCheckBreakdown(target);
          return;

        default:
          // The backend can ship an action this bundle predates. Doing nothing is
          // the right answer — the alternative is a control that throws.
          logger.warn("[dashboard] unknown widget action", action);
      }
    },
    [openRoom, openSheet, openPayBreakdown, openCheckBreakdown],
  );
}
